import React, { useState } from 'react';
import { Campsite, NormalizedDogWalk } from '../types';
import { mentionsDogs } from '../utils/mentionsDogs';
import { fetchReviews } from '../utils/api';

interface ReviewListProps {
  site: Campsite | NormalizedDogWalk;
  onClose: () => void;
  onAddReview?: (site: Campsite | NormalizedDogWalk) => void;
}

const ReviewList: React.FC<ReviewListProps> = ({ site, onClose, onAddReview }) => {
  const [showDogOnly, setShowDogOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const isDogWalk = 'type' in site && site.type === 'dogwalk';

  const reviewsToShow = showDogOnly && !isDogWalk
    ? site.reviews?.filter(r => mentionsDogs(r.review?.text || (r as any).comment || ''))
    : site.reviews;

  const siteId = 'ID' in site ? site.ID : String(site.id);
  const siteName = 'Name' in site ? site.Name : site.name;

  // Extract site details safely
  const siteDescription = 'description' in site ? site.description : (site as any).description;
  const siteImage = 'thumb' in site ? site.thumb : null;
  const siteTown = 'town' in site ? site.town : null;
  const siteDistance = 'dis' in site ? site.dis : null;
  const featuredImage = 'featured_image_path' in site ? site.featured_image_path : null;

  const loadMore = async () => {
    setIsLoading(true);
    // For campsites only, fetch from external API with pagination
    const more = await fetchReviews(siteId, offset + 10);
    site.reviews = [...(site.reviews || []), ...more];
    setOffset(offset + 10);
    setIsLoading(false);
  };

  return (
    <div className="review-list-panel" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5em' }}>{siteName}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5em', cursor: 'pointer' }}>✖</button>
      </div>

      {/* Site Details Section */}
      <div style={{ marginBottom: '20px', borderBottom: '1px solid #ddd', paddingBottom: '15px' }}>
        {(siteImage || featuredImage) && (
          <img
            src={
              featuredImage
                ? (featuredImage.startsWith('http') ? featuredImage : `https://www.dogwalksnearme.co.uk/${featuredImage}`)
                : `https://www.searchforsites.co.uk/images/thumbs/${siteImage}`
            }
            alt={siteName}
            style={{ width: '100%', borderRadius: '8px', marginBottom: '10px', objectFit: 'cover', maxHeight: '200px' }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}

        {siteTown && <p style={{ margin: '5px 0', color: '#555' }}><strong>Location:</strong> {siteTown} {siteDistance && `(${siteDistance})`}</p>}

        {siteDescription && (
          <div style={{ margin: '10px 0', lineHeight: '1.5', color: '#333', fontSize: '0.95em' }}>
            {siteDescription}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0 }}>Reviews</h3>
        {onAddReview && (
          <button
            onClick={() => onAddReview(site)}
            style={{
              padding: '5px 10px',
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9em'
            }}
          >
            ✍️ Add Review
          </button>
        )}
      </div>

      {/* Only show dog filter for campsites, not dog walks */}
      {!isDogWalk && (
        <button onClick={() => setShowDogOnly(!showDogOnly)} style={{ marginBottom: '10px', width: '100%', padding: '8px' }}>
          {showDogOnly ? 'Show All Reviews' : '🐶 Show Dog Mentions Only'}
        </button>
      )}

      <ul style={{ maxHeight: 'calc(100vh - 500px)', overflowY: 'auto', padding: '0', listStyle: 'none' }}>
        {reviewsToShow?.length ? reviewsToShow.map((entry: any, i) => {
          // Handle both complex ReviewEntry and simple { user, stars, comment } structures
          const userName = typeof entry.user === 'string' ? entry.user : entry.user?.name || 'Anonymous';
          const reviewDate = entry.review?.date || 'Unknown date';
          const reviewText = entry.review?.text || entry.comment || '';
          const reviewScore = entry.review?.rating?.score || entry.stars || 0;

          // Normalize score to 5-point scale
          const normalizedScore = reviewScore > 5 ? reviewScore / 2 : reviewScore;

          return (
            <li key={i} style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{userName}</strong>
                <span style={{ color: '#666', fontSize: '0.9em' }}>{reviewDate}</span>
              </div>
              <div style={{ margin: '5px 0' }}>{reviewText}</div>
              <div style={{ color: '#f5b301' }}>
                {'★'.repeat(Math.round(normalizedScore))}
                <span style={{ color: '#666', fontSize: '0.8em' }}> ({normalizedScore}/5)</span>
              </div>
            </li>
          );
        }) : (
          <li style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No reviews found.</li>
        )}
      </ul>

      {/* Only show "Load more reviews" button for campsites, not dog walks */}
      {!isDogWalk && (
        <button onClick={loadMore} disabled={isLoading} style={{ marginTop: '10px', width: '100%', padding: '10px' }}>
          {isLoading ? 'Loading...' : 'Load more reviews'}
        </button>
      )}
    </div>
  );
};

export default ReviewList;